import type { GuardResult, GuardContext, Guard } from './types';
import { isActiveUser, isSuperuser, hasStreamAccess } from './types';

export async function applyGuards(guards: Guard[], context: GuardContext): Promise<GuardResult> {
	for (const guard of guards) {
		const result = await guard(context);

		if (!result.allowed) {
			return result;
		}
	}

	return { allowed: true };
}

export function composeGuards(...guards: Guard[]): Guard {
	return async (context: GuardContext): Promise<GuardResult> => {
		return applyGuards(guards, context);
	};
}

export function requireAuth(context: GuardContext): GuardResult {
	if (!context.user) {
		return {
			allowed: false,
			redirect: '/signin',
			reason: 'Authentication required'
		};
	}

	return { allowed: true };
}

export function requireNotBanned(context: GuardContext): GuardResult {
	if (context.user?.banned) {
		return {
			allowed: false,
			redirect: '/',
			reason: 'Account is banned'
		};
	}

	return { allowed: true };
}

export function requireSuperuser(context: GuardContext): GuardResult {
	if (!context.user) {
		return {
			allowed: false,
			redirect: '/signin',
			reason: 'Authentication required'
		};
	}

	if (!isSuperuser(context.user)) {
		return {
			allowed: false,
			redirect: '/admin',
			reason: 'Superuser access required'
		};
	}

	return { allowed: true };
}

export function requireActiveUser(context: GuardContext): GuardResult {
	if (!context.user) {
		return {
			allowed: false,
			redirect: '/signin',
			reason: 'Authentication required'
		};
	}

	if (!isActiveUser(context.user)) {
		return {
			allowed: false,
			redirect: '/onboard',
			reason: 'Active streamer status required - complete onboarding'
		};
	}

	return { allowed: true };
}

export function requireOnboardingComplete(context: GuardContext): GuardResult {
	if (!context.user) {
		return {
			allowed: false,
			redirect: '/signin',
			reason: 'Authentication required'
		};
	}

	if (!isActiveUser(context.user)) {
		return {
			allowed: false,
			redirect: '/onboard',
			reason: 'Onboarding incomplete'
		};
	}

	return { allowed: true };
}

export function createStreamAccessGuard(tag: string) {
	return function requireStreamAccess(context: GuardContext): GuardResult {
		if (!context.user) {
			return {
				allowed: false,
				redirect: '/signin',
				reason: 'Authentication required'
			};
		}

		if (!hasStreamAccess(context.user, tag)) {
			return {
				allowed: false,
				redirect: '/admin',
				reason: `Access to show '${tag}' denied`
			};
		}

		return { allowed: true };
	};
}

export function redirectIfAuthenticated(redirectTo: string = '/admin') {
	return function (context: GuardContext): GuardResult {
		if (context.user && !context.user.banned) {
			return {
				allowed: false,
				redirect: redirectTo,
				reason: 'Already authenticated'
			};
		}

		return { allowed: true };
	};
}

export function redirectIfComplete(redirectTo: string = '/admin') {
	return function (context: GuardContext): GuardResult {
		if (context.user && isActiveUser(context.user)) {
			return {
				allowed: false,
				redirect: redirectTo,
				reason: 'Onboarding already complete'
			};
		}

		return { allowed: true };
	};
}
